import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import type { Color } from "@deck.gl/core";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { Map, useMap } from "@vis.gl/react-google-maps";
import { FC, useEffect, useMemo } from "react";

const DARK_STYLE_MINIMAL_LABELS = [
  {
    elementType: "geometry",
    stylers: [
      {
        color: "#212121",
      },
    ],
  },
  {
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#212121",
      },
    ],
  },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    featureType: "administrative.country",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#9e9e9e",
      },
    ],
  },
  {
    featureType: "administrative.land_parcel",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#bdbdbd",
      },
    ],
  },
  {
    featureType: "administrative.neighborhood",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "administrative.province",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "landscape.man_made",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    featureType: "poi.business",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [
      {
        color: "#181818",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#616161",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#1b1b1b",
      },
    ],
  },
  {
    featureType: "road",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#2c2c2c",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#8a8a8a",
      },
    ],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [
      {
        color: "#373737",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [
      {
        color: "#3c3c3c",
      },
    ],
  },
  {
    featureType: "road.highway.controlled_access",
    elementType: "geometry",
    stylers: [
      {
        color: "#4e4e4e",
      },
    ],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#616161",
      },
    ],
  },
  {
    featureType: "transit",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "transit",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [
      {
        color: "#000000",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels.text",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
];

const HEATMAP_COLORS: Color[] = [
  [0, 228, 0, 255], //green
  [255, 255, 0, 255], //yellow
  [255, 126, 0, 255], //orange
  [255, 0, 0, 255], //red
];

export const VerificationHeatMap = ({
  data,
}: {
  data?: Array<[number, number]>;
}) => (
  <div className="flex w-full h-[380px] rounded-lg relative overflow-hidden">
    <Map
      controlled={false}
      cameraControl={false}
      keyboardShortcuts={false}
      fullscreenControl={false}
      mapTypeControl={false}
      scaleControl={false}
      streetViewControl={false}
      defaultZoom={1.6}
      defaultCenter={{ lat: 15, lng: 5 }}
      styles={DARK_STYLE_MINIMAL_LABELS}
      className="w-full"
    />
    <Heatmap latLong={data} radius={12} opacity={0.65} />
  </div>
);

type Props = {
  latLong?: Array<[number, number]>;
  radius: number;
  opacity: number;
};

const Heatmap: FC<Props> = ({ latLong, radius, opacity }) => {
  const map = useMap();

  const heatmapData = useMemo(() => {
    if (!latLong || latLong.length === 0) return [];
    return latLong.map(([lat, lng]) => ({
      position: [lng, lat],
      weight: 1,
    }));
  }, [latLong]);

  const heatmap = useMemo(() => {
    if (!heatmapData.length) return null;
    return new HeatmapLayer({
      id: "HeatmapLayer",
      data: heatmapData,
      getPosition: (d) => d.position,
      getWeight: (d) => d.weight,
      aggregation: "SUM",
      radiusPixels: radius,
      opacity: opacity,
      colorRange: HEATMAP_COLORS,
    });
  }, [radius, opacity, heatmapData]);

  useEffect(() => {
    if (!map || !heatmap) return;

    const overlay = new GoogleMapsOverlay({
      layers: [heatmap],
    });

    overlay.setMap(map);

    return () => {
      overlay.setMap(null);
    };
  }, [map, heatmap]);

  return null;
};
